import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { PayrollCycleFragment, PayrollCyclesDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { CyclesLocationCallout } from 'modules/bookkeepers/components/CyclesLocationCallout'
import useGetCycleCount from 'modules/bookkeepers/hooks/useGetCycleCount'
import useGetPayrollResults from 'modules/bookkeepers/hooks/useGetPayrollResults'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'

function PayrollCyclesPage() {
  const { selectedCompany, filterDate, setSelectedCompany, setFilterDate } =
    useIncidenceFilterContext()
  const { cyclesCount } = useGetCycleCount()
  const { payrollResults } = useGetPayrollResults()
  const payrollResultsCount = payrollResults.length

  const showCallout = cyclesCount > 0 && payrollResultsCount > 0

  const bookkeeperContext = {
    isBookkeeperView: true,
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: PayrollCyclesDocument,
      queryContext: bookkeeperContext,
      mapData: (data): PayrollCycleFragment[] => {
        return data.compensations.payrollRunsConnection.nodes
          .map((item) => {
            return {
              ...item,
            }
          })
          .sort((a, b) => {
            const dateA = a.endsOn || a.startsOn || ''
            const dateB = b.endsOn || b.startsOn || ''
            return dateB.localeCompare(dateA)
          })
      },
      mapPagination: (data) => {
        return {
          total: data.compensations.payrollRunsConnection.totalCount ?? 0,
        }
      },
      perPage: 10,
      mapVariables: () => {
        return {
          ...(selectedCompany && { companyIds: [selectedCompany.attributes.id] }),
          ...(filterDate.firstDate && { dateFrom: filterDate.firstDate }),
          ...(filterDate.lastDate && { dateTo: filterDate.lastDate }),
        }
      },
    },
    [selectedCompany?.attributes.id, filterDate.firstDate, filterDate.lastDate]
  )

  return (
    <Box
      width="full"
      style={{
        maxWidth: '1440',
      }}
      padding="s12"
    >
      <ResourceHeader title={i18n.t('bookkeepers.clients.payroll_cycles.title')} />
      {showCallout && (
        <Box padding="s12">
          <CyclesLocationCallout />
        </Box>
      )}
      <OneDataCollection
        emptyStates={{
          'no-results': {
            emoji: '📑',
            title: i18n.t('bookkeepers.clients.payroll_cycles.empty_state.title'),
            description: i18n.t('bookkeepers.clients.payroll_cycles.empty_state.description'),
          },
          'no-data': {
            emoji: '🧐',
            title: i18n.t('bookkeepers.clients.payroll_cycles.empty_state.no_results_title'),
            description: i18n.t(
              'bookkeepers.clients.payroll_cycles.empty_state.no_results_description'
            ),
            actions: [
              {
                label: i18n.t('bookkeepers.clients.payroll_cycles.empty_state.cta'),
                onClick: () => {
                  setSelectedCompany(null)
                  setFilterDate({})
                },
              },
            ],
          },
        }}
        source={source}
        visualizations={[
          {
            type: 'list',
            options: {
              itemDefinition: (item: PayrollCycleFragment) => ({
                title: item.cycle?.company?.name || '',
              }),
              fields: [
                {
                  label: i18n.t('bookkeepers.clients.payroll_cycles.list_columns.period'),
                  render: (item: PayrollCycleFragment) => item.label,
                },
              ],
            },
          },
        ]}
      />
    </Box>
  )
}

export default PayrollCyclesPage
